import { useEffect, useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useCurrentGroup } from 'components/IAP/hooks/commonHooks';
import type { ExternalLayerAccessType } from 'hooks/externalLayer/types';
import { useListExternalLayersQuery } from 'hooks/externalLayer/useListExternalLayersQuery';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';
import {
  automationReadinessLabel,
  isArcGisServiceRoot,
  withSublayerIndex,
} from './arcgisLayerSchema';
import { DEMO_ARCGIS_LAYER_CATALOG } from './demoArcGisLayerCatalog';
import {
  fetchArcGisSublayers,
  type ArcGisSublayerInfo,
} from './useArcGisLayerSchema';
import { usePortalUserItems } from '../LayersTab/arcgis-integration/arcGisPortal';
import { useArcGisAuthorization } from '../LayersTab/utils/useArcGisAuthorization';

export type PickedArcGisLayer = {
  serviceUrl: string;
  layerLabel: string;
  authType: ExternalLayerAccessType;
  externalLayerId?: string;
  sublayerId?: number;
  mockOnly?: boolean;
};

type ArcGisLayerPickerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (picked: PickedArcGisLayer) => void;
  initialUrl?: string;
};

type PendingSelection = {
  serviceUrl: string;
  layerLabel: string;
  authType: ExternalLayerAccessType;
  externalLayerId?: string;
  mockOnly?: boolean;
};

function ReadinessBadge({
  authType,
}: {
  authType: ExternalLayerAccessType | undefined;
}) {
  const readiness = automationReadinessLabel(authType);
  return (
    <Badge
      variant="outline"
      className={
        readiness.runsInBackground
          ? 'tw:border-emerald-500/40 tw:text-emerald-300'
          : 'tw:border-amber-500/40 tw:text-amber-300'
      }
    >
      {readiness.label}
    </Badge>
  );
}

export function ArcGisLayerPicker({
  open,
  onOpenChange,
  onPick,
  initialUrl,
}: ArcGisLayerPickerProps) {
  const groupGuid = useAppSelector(selectGroupGuid);
  const group = useCurrentGroup();
  const { isArcGISAuthorized, authorizeArcGIS, isCheckingArcGISAuthorization } =
    useArcGisAuthorization();

  const [tab, setTab] = useState('demo');
  const [pending, setPending] = useState<PendingSelection | null>(null);
  const [urlDraft, setUrlDraft] = useState(initialUrl ?? '');
  const [sublayers, setSublayers] = useState<ArcGisSublayerInfo[]>([]);
  const [sublayerId, setSublayerId] = useState<number | null>(null);
  const [sublayerError, setSublayerError] = useState<string | null>(null);
  const [sublayerLoading, setSublayerLoading] = useState(false);
  const [portalSearch, setPortalSearch] = useState('');

  const { data: savedLayersResp, isLoading: savedLoading } =
    useListExternalLayersQuery(
      {
        filter: { groupGuid: groupGuid ?? '' },
        perPage: 100,
      },
      { enabled: open && Boolean(groupGuid) }
    );
  const savedLayers = savedLayersResp?.data ?? [];

  const portalState = useMemo(
    () => ({
      searchTerm: portalSearch || null,
      esriItem: 'Feature Service' as const,
      owner: 'Private' as const,
      sort: 'modified' as const,
    }),
    [portalSearch]
  );
  const fallbackGroup = useMemo((): Group => {
    if (group) return group;
    return {
      group_guid: '',
      group_name: '',
      default_settings: null,
      risk_levels: [],
      daily_digest_team: '',
      group_member_guid: '',
      user_guid: '',
      role: '',
      allow_admin_profile_edits: false,
      oauth_client_id: undefined,
      arcgis_portal_url: undefined,
    };
  }, [group]);
  const { data: portalData, isLoading: portalLoading } = usePortalUserItems({
    portalState,
    group: fallbackGroup,
  });
  const portalItems =
    isArcGISAuthorized && group?.oauth_client_id
      ? (portalData?.items ?? [])
      : [];

  useEffect(() => {
    if (!open) return;
    setUrlDraft(initialUrl ?? '');
    setPending(null);
    setSublayers([]);
    setSublayerId(null);
    setSublayerError(null);
    setTab(savedLayers.length > 0 ? 'saved' : 'demo');
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps -- only reset on open

  useEffect(() => {
    if (!pending?.serviceUrl) {
      setSublayers([]);
      setSublayerId(null);
      return;
    }
    if (!isArcGisServiceRoot(pending.serviceUrl)) {
      setSublayers([]);
      setSublayerId(null);
      return;
    }
    let cancelled = false;
    setSublayerLoading(true);
    setSublayerError(null);
    void fetchArcGisSublayers(pending.serviceUrl)
      .then((layers) => {
        if (cancelled) return;
        setSublayers(layers);
        setSublayerId(layers[0]?.id ?? null);
      })
      .catch((err: { message?: string }) => {
        if (cancelled) return;
        setSublayerError(err?.message || 'Could not list sublayers.');
        setSublayers([]);
      })
      .finally(() => {
        if (!cancelled) setSublayerLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pending?.serviceUrl]);

  const confirmPick = () => {
    if (!pending) return;
    let serviceUrl = pending.serviceUrl.trim();
    let resolvedSublayer: number | undefined;
    if (isArcGisServiceRoot(serviceUrl) && sublayerId != null) {
      serviceUrl = withSublayerIndex(serviceUrl, sublayerId);
      resolvedSublayer = sublayerId;
    }
    onPick({
      serviceUrl,
      layerLabel: pending.layerLabel,
      authType: pending.authType,
      externalLayerId: pending.externalLayerId,
      sublayerId: resolvedSublayer,
      mockOnly: pending.mockOnly,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="dark tw:max-w-lg tw:bg-background tw:text-foreground">
        <DialogHeader>
          <DialogTitle>Choose ArcGIS layer</DialogTitle>
          <DialogDescription>
            Pick a public demo layer, one of your saved layers, search your
            portal, or paste a FeatureServer URL.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={(value) => setTab(String(value))}>
          <TabsList variant="line" className="tw:w-full tw:justify-start">
            <TabsTrigger value="demo">Demo</TabsTrigger>
            <TabsTrigger value="saved">Saved</TabsTrigger>
            <TabsTrigger value="search">Search ArcGIS</TabsTrigger>
            <TabsTrigger value="url">URL</TabsTrigger>
          </TabsList>

          <TabsContent value="demo" className="tw:mt-3 tw:space-y-2">
            {DEMO_ARCGIS_LAYER_CATALOG.map((layer) => {
              const selected = pending?.serviceUrl === layer.serviceUrl;
              return (
                <button
                  key={layer.id}
                  type="button"
                  className={`tw:flex tw:w-full tw:flex-col tw:gap-1 tw:rounded-md tw:border tw:px-3 tw:py-2 tw:text-left tw:transition-colors ${
                    selected
                      ? 'tw:border-primary tw:bg-muted/50'
                      : 'tw:border-border tw:hover:bg-muted/30'
                  }`}
                  onClick={() =>
                    setPending({
                      serviceUrl: layer.serviceUrl,
                      layerLabel: layer.name,
                      authType: layer.authType,
                      mockOnly: layer.mockOnly,
                    })
                  }
                >
                  <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
                    <span className="tw:text-sm tw:font-medium">
                      {layer.name}
                    </span>
                    <ReadinessBadge authType={layer.authType} />
                  </div>
                  <span className="tw:text-xs tw:text-muted-foreground">
                    {layer.description}
                  </span>
                </button>
              );
            })}
          </TabsContent>

          <TabsContent value="saved" className="tw:mt-3 tw:space-y-2">
            {savedLoading ? (
              <Skeleton className="tw:h-16 tw:w-full" />
            ) : savedLayers.length === 0 ? (
              <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                No saved external layers yet. Import layers from the Layers tab
                or use Demo / URL.
              </p>
            ) : (
              savedLayers.map((layer) => {
                const url = layer.serviceUrl || '';
                const selected =
                  pending?.externalLayerId === layer.id ||
                  pending?.serviceUrl === url;
                return (
                  <button
                    key={layer.id}
                    type="button"
                    disabled={!url}
                    className={`tw:flex tw:w-full tw:flex-col tw:gap-1 tw:rounded-md tw:border tw:px-3 tw:py-2 tw:text-left tw:transition-colors tw:disabled:opacity-50 ${
                      selected
                        ? 'tw:border-primary tw:bg-muted/50'
                        : 'tw:border-border tw:hover:bg-muted/30'
                    }`}
                    onClick={() =>
                      setPending({
                        serviceUrl: url,
                        layerLabel: layer.name,
                        authType: layer.authType || 'public',
                        externalLayerId: String(layer.id),
                      })
                    }
                  >
                    <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
                      <span className="tw:text-sm tw:font-medium">
                        {layer.name}
                      </span>
                      <ReadinessBadge authType={layer.authType} />
                    </div>
                    <span className="tw:truncate tw:text-[11px] tw:text-muted-foreground">
                      {url || 'No service URL'}
                    </span>
                  </button>
                );
              })
            )}
          </TabsContent>

          <TabsContent value="search" className="tw:mt-3 tw:space-y-2">
            {!isArcGISAuthorized ? (
              <div className="tw:space-y-2">
                <FieldDescription>
                  Connect ArcGIS to search your content, favorites, groups, and
                  org.
                </FieldDescription>
                <Button
                  type="button"
                  size="sm"
                  disabled={
                    isCheckingArcGISAuthorization || !group?.oauth_client_id
                  }
                  onClick={() => void authorizeArcGIS()}
                >
                  {isCheckingArcGISAuthorization
                    ? 'Checking…'
                    : 'Connect ArcGIS'}
                </Button>
              </div>
            ) : (
              <>
                <Input
                  placeholder="Search portal items…"
                  value={portalSearch}
                  onChange={(e) => setPortalSearch(e.target.value)}
                />
                <div className="tw:max-h-48 tw:space-y-1 tw:overflow-y-auto">
                  {portalLoading ? (
                    <Skeleton className="tw:h-16 tw:w-full" />
                  ) : portalItems.length === 0 ? (
                    <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                      No matching Feature Services found.
                    </p>
                  ) : (
                    portalItems
                      .filter(
                        (item) =>
                          item.type === 'Feature Service' ||
                          item.type === 'Feature Layer' ||
                          Boolean(item.url)
                      )
                      .slice(0, 40)
                      .map((item) => {
                        const url = item.url || '';
                        const selected = pending?.serviceUrl === url;
                        return (
                          <button
                            key={item.id}
                            type="button"
                            disabled={!url}
                            className={`tw:flex tw:w-full tw:flex-col tw:gap-0.5 tw:rounded-md tw:border tw:px-2 tw:py-1.5 tw:text-left ${
                              selected
                                ? 'tw:border-primary tw:bg-muted/50'
                                : 'tw:border-border tw:hover:bg-muted/30'
                            }`}
                            onClick={() =>
                              setPending({
                                serviceUrl: url,
                                layerLabel: item.title || 'Untitled',
                                authType: 'userAuth',
                              })
                            }
                          >
                            <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
                              <span className="tw:text-sm">
                                {item.title || 'Untitled'}
                              </span>
                              <ReadinessBadge authType="userAuth" />
                            </div>
                            <span className="tw:text-[11px] tw:text-muted-foreground">
                              {item.type}
                            </span>
                          </button>
                        );
                      })
                  )}
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="url" className="tw:mt-3 tw:space-y-2">
            <Field>
              <FieldLabel>FeatureServer layer URL</FieldLabel>
              <Input
                placeholder="…/FeatureServer/0"
                value={urlDraft}
                onChange={(e) => setUrlDraft(e.target.value)}
              />
            </Field>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!urlDraft.trim()}
              onClick={() =>
                setPending({
                  serviceUrl: urlDraft.trim(),
                  layerLabel: 'Custom ArcGIS layer',
                  authType: 'public',
                })
              }
            >
              Use this URL
            </Button>
          </TabsContent>
        </Tabs>

        {pending && isArcGisServiceRoot(pending.serviceUrl) ? (
          <Field className="tw:mt-2">
            <FieldLabel>Sublayer</FieldLabel>
            {sublayerLoading ? (
              <Skeleton className="tw:h-9 tw:w-full" />
            ) : sublayerError ? (
              <p className="tw:m-0 tw:text-xs tw:text-destructive">
                {sublayerError}
              </p>
            ) : sublayers.length === 0 ? (
              <FieldDescription>
                No sublayers found. Confirm the service URL is correct.
              </FieldDescription>
            ) : (
              <Select
                value={sublayerId != null ? String(sublayerId) : undefined}
                onValueChange={(v) => setSublayerId(Number(String(v)))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose sublayer" />
                </SelectTrigger>
                <SelectContent className="dark">
                  {sublayers.map((layer) => (
                    <SelectItem key={layer.id} value={String(layer.id)}>
                      {layer.id}: {layer.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
        ) : null}

        {pending?.authType === 'userAuth' ? (
          <p className="tw:m-0 tw:mt-2 tw:text-xs tw:text-amber-300">
            This layer requires your ArcGIS sign-in and will not run in the
            background scheduler until org-key access is configured.
          </p>
        ) : null}

        <DialogFooter className="tw:mt-4">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={
              !pending ||
              (isArcGisServiceRoot(pending.serviceUrl) &&
                (sublayerLoading || sublayerId == null))
            }
            onClick={confirmPick}
          >
            Use layer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
